import { useSnackbar } from 'notistack';
import { useState, useEffect, Fragment } from 'react';
// next
import { useRouter } from 'next/router';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
  TextField,
  Stack,
  Typography,
  Autocomplete,
  MenuItem,
  Checkbox,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { StaticDatePicker } from '@mui/x-date-pickers-pro';
import { createFilterOptions } from '@mui/material/Autocomplete';
// components
import SvgIconStyle from '../../components/SvgIconStyle';
// utils
import { fKrDateKST, fYYYYMMDD } from '../../utils/formatTime';
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const PASS_OPTIONS = [
  {
    value: "지금은 너무 바빠요.",
    description: <Typography variant="body2" sx={{ color: "text.secondary" }}>파트타임 업무를 할 여력이 되지 않아서 거절하시는 경우에 선택해주세요.</Typography>,
    svgSrc: `/icons/icon-clock-mono.svg`
  },
  {
    value: "조건이 마음에 들지 않아요.",
    description: <Typography variant="body2" sx={{ color: "text.secondary" }}>예상보다 기간이 짧거나, 금액이 낮아서 거절하시는 경우에 선택해주세요.</Typography>,
    svgSrc: `/icons/icon-document-mono.svg`
  },
  {
    value: "관심 분야가 아니에요.",
    description: <Typography variant="body2" sx={{ color: "text.secondary" }}><b>키워드 필터</b>로 관심 없는 공고를 제외하고 원하는 매칭만 받을 수 있습니다.</Typography>,
    svgSrc: `/icons/icon-filter-mono.svg`
  },
  {
    value: "직접입력",
    description: <Typography variant="body2" sx={{ color: "text.secondary" }}>다른 사유가 있으시면 직접 입력해주세요.</Typography>,
    svgSrc: `/icons/icon-pencil-mono.svg`
  },
];

const filter = createFilterOptions();

export default function OfferPassDialog({ 
  passDialog,
  closePassDialog,
  reloadOffer,
}) {
  const { user } = useAuth();
  const [reason, setReason] = useState(null);

  // 제안 끄기
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarMaxDate = new Date(new Date().getFullYear(), new Date().getMonth()+2, new Date().getDate());
  const [value, setValue] = useState(null);

  // 필터 설정하기
  const [showFilter, setShowFilter] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [systemKeywords, setSystemKeywords] = useState([]);
  const [jobPostKeywords, setJobPostKeywords] = useState([]);
  const [jobPostKeywordSelected, setJobPostKeywordSelected] = useState(false);

  // 기타 사유
  const [otherReason, setOtherReason] = useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { oid } = router.query;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const smUp = useResponsive('up', 'sm');

  const step = showCalendar ? "calendar" : showFilter ? "filter" : "pass";

  const canSubmit = reason && (reason !== '직접입력' || (reason === '직접입력' && otherReason && otherReason.trim()));


  const resetAllState = async () => {
    setReason(null);
    setShowCalendar(false);
    setValue(null);
    setShowFilter(false);
    setKeywords([]);
    setSystemKeywords([]);
    setJobPostKeywords([]);
    setJobPostKeywordSelected(false);
    setOtherReason(null);
  };

  const initializeKeywords = async () => {
    try {
      axios.get(`/api/crews/settings/keyword-filter/?job_post_uuid=${oid}`).then((response) => {
        setSystemKeywords(response?.data?.system_keywords);
        const userKeywordsNotInSystem = response?.data?.user_keywords.filter(
          (userKeyword) => !response.data.system_keywords.some(
            (systemKeyword) => systemKeyword.keyword === userKeyword.keyword
          )
        );
        if (userKeywordsNotInSystem.length > 0) {
          setSystemKeywords((prev) => [...prev, ...userKeywordsNotInSystem]);
        }
        setKeywords(response?.data?.user_keywords);
        setJobPostKeywords(response?.data?.job_post_keywords?.map((k) => ({...k, selected: false})));
        setJobPostKeywordSelected(response?.data?.job_post_keywords?.length === 0);
      });
    } catch (error) {
      console.error(error);
    };
  };

  const selectJobPostKeywords = async () => {
    const selectedKeywords = jobPostKeywords
      .filter((k) => k.selected)
      .map((k) => ({
        keyword: k.keyword,
        description: k.description,
        inputValue: ""
      }));

    setKeywords((prev) => {
      const newKeywords = [...prev];
      selectedKeywords.forEach((selectedKeyword) => {
        if (!newKeywords.some((keyword) => keyword.keyword === selectedKeyword.keyword)) {
          newKeywords.push(selectedKeyword);
        }
      });
      return newKeywords;
    });
    setJobPostKeywordSelected(true);
  };

  const checkJobPostKeyword = (k, index) => {
    setJobPostKeywords((prev) => {
      const newJobPostKeywords = [...prev];
      newJobPostKeywords[index].selected = !newJobPostKeywords[index].selected;
      return newJobPostKeywords;
    });
  };

  const handleKeywordChange = (event, newValues, reason) => {
    const stringValue = newValues.find((value) => typeof value === 'string');
    const newKeyword = stringValue ? { keyword: stringValue, inputValue: "" } : null;

    if (reason === "createOption" && newKeyword) {
      if (!systemKeywords.some((option) => option.keyword === stringValue)) {
        setSystemKeywords((prev) => [...prev, newKeyword]);
      }
      setKeywords((prev) => {
        if (!prev.some((keyword) => keyword.keyword === newKeyword.keyword)) {
          return [...prev, newKeyword];
        }
        return prev.filter((keyword) => keyword.keyword !== newKeyword.keyword);
      });
    } else {
      const addedValues = newValues.filter(
        (val) => !systemKeywords.some((option) => option.keyword === val.keyword)
      ).map((val) => ({ ...val, inputValue: "" }));

      if (addedValues.length) {
        setSystemKeywords((prev) => [...prev, ...addedValues]);
      }
      setKeywords(newValues);
    }
  };

  const submit = async (data) => {
    try {
      setIsSubmitting(true);
      const postURL = `/api/crews/offers/${oid}/pass/`;
      await axios.post(postURL, {
        reason: data.reason,
      });
      enqueueSnackbar('제안을 거절하였습니다');
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
      if (error.response && error.response.status === 400 && error.response.data) {
        enqueueSnackbar(error.response.data.message, { variant: 'warning' });
      } else {
        enqueueSnackbar('저장에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      }
    }
  };

  useEffect(() => {
    if (passDialog) {
      resetAllState();
      initializeKeywords();
    }
  }, [passDialog]);


  const submitForm = async () => {
    const submitData = {
      reason: reason === '직접입력' ? otherReason : reason,
    }
    await submit(submitData);
    if (reason === '지금은 너무 바빠요.' && user?.crew?.is_matching_allowed) {
      setShowCalendar(true);
    } else if (reason === '관심 분야가 아니에요.') {
      setShowFilter(true);
    } else {
      reloadOffer();
      closePassDialog();
    }
  };

  const turnOffMatching = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(`api/crews/offers/turn-off/`, {
        until: fYYYYMMDD(value),
      });
      enqueueSnackbar('제안받기를 중단하였습니다');
      setIsSubmitting(false);
      reloadOffer();
      closePassDialog();
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
  };

  const updateMatchingFilter = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(`/api/crews/settings/keyword-filter/`, { keywords });
      enqueueSnackbar('키워드 필터가 저장되었습니다.', { variant: 'success' });
      setIsSubmitting(false);
      reloadOffer();
      closePassDialog();
    } catch (error) {
      setIsSubmitting(false);
      enqueueSnackbar('키워드 필터 저장에 실패했습니다. 다시 시도해주세요.', { variant: 'error' });
      console.error(error);
    }
  };


  const matchingContinues = async () => {
    try {
      reloadOffer();
      closePassDialog();
    } catch (error) {
      console.error(error);
    }
  }

  return (
      <Dialog
        open={passDialog}
        fullWidth
        maxWidth="dg"
      >
      {step === "calendar" && (
        <DialogTitle gutterBottom>제안받기를 잠시 멈추고 싶으신가요?</DialogTitle>
      )}
      {step === "pass" && (
        <DialogTitle gutterBottom>제안을 거절하시는 이유를 알려주세요.</DialogTitle>
      )}
      {step === "filter" && (
        <DialogTitle gutterBottom>관심 없는 키워드 제외하기</DialogTitle>
      )}
      {step === "pass" && (
      <>
      <DialogContent>
        <>
        <Typography variant="body1" paragraph>
          선택하신 내용은 기업에 공유되지 않으며,<br />더 만족스러운 제안을 위해서 여쭙습니다.
        </Typography>
        <Stack spacing={2}>
        {PASS_OPTIONS.map((option, index) => (
          <Stack
            key={index}
            direction="row"
            alignItems="flex-start"
            spacing={1}
            onClick={() => setReason(option.value)}
            sx={{
              border: 1,
              borderColor: (theme) => option.value === reason ? theme.palette.common.black : theme.palette.grey[500_32],
              borderRadius: 1.5,
              p: 2,
              cursor: 'pointer',
              "&:hover": {
                borderColor: (theme) => theme.palette.common.black,
                backgroundColor: (theme) => theme.palette.grey[500_16],
              },
              ...option.value === reason && {
                borderColor: (theme) => theme.palette.grey[600],
                backgroundColor: (theme) => theme.palette.grey[200],
              }
            }}
          >
            <SvgIconStyle
              src={option.svgSrc}
              sx={{
                color: "text.secondary", minWidth: "22px", minHeight: "22px", maxWidth: "22px", maxHeight: "22px",
                ...option.value === reason && {
                  color: "text.primary",
                }
              }}
            />
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1">{option.value}</Typography>
              {option.description}
            </Stack>
            {smUp && <Radio color="primary" checked={option.value === reason} sx={{ top: "-8px", right: "-8px" }} />}
          </Stack>
        ))}
        </Stack>
        {reason === '직접입력' && (
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="거절 사유를 입력해주세요."
            value={otherReason || ''}
            onChange={(e) => setOtherReason(e.target.value)}
            sx={{ mt: 2 }}
            helperText="구체적인 사유를 입력해주시면 더 좋은 매칭을 위해 참고하겠습니다."
          />
        )}
        </>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          sx={{ mr: 2, px: 2 }}
          onClick={closePassDialog}
          loading={isSubmitting}
          size="large"
        >
          취소하기
        </LoadingButton>
        <LoadingButton
          onClick={submitForm}
          variant="contained"
          disabled={!canSubmit}
          loading={isSubmitting}
          size="large"
        >
          거절하기
        </LoadingButton>
      </DialogActions>
      </>
      )}
      {step === "calendar" && (
      <>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {value ? fKrDateKST(value) : '날짜를 선택해주시면 그 전'}까지는 매칭 제안을 보내지 않을게요.
          </DialogContentText>
          <StaticDatePicker
            value={value}
            displayStaticWrapperAs={'desktop'}
            disablePast
            maxDate={calendarMaxDate}
            closeOnSelect={false}
            onChange={(newValue) => {
              setValue(newValue);
            }}
            renderInput={(props) => (
              <Fragment>
                <TextField {...props} />
              </Fragment>
            )}
          />
        </DialogContent>
        {smUp && (
        <DialogActions>
          <Stack spacing={3} direction="row">
            <LoadingButton
              onClick={matchingContinues}
              loading={isSubmitting}
            >
              제안을 계속 받을래요
            </LoadingButton>
            <LoadingButton
              onClick={turnOffMatching}
              variant="contained"
              disabled={!value}
              loading={isSubmitting}
            >
              제안을 멈춰주세요
            </LoadingButton>
          </Stack>
        </DialogActions>
        )}
        {!smUp && (
        <Stack sx={{ mx: 2, my: 3 }} spacing={2}>
          <LoadingButton
            onClick={turnOffMatching}
            variant="contained"
            disabled={!value}
            loading={isSubmitting}
          >
            제안을 멈춰주세요
          </LoadingButton>
          <LoadingButton
            onClick={matchingContinues}
            loading={isSubmitting}
          >
            제안을 계속 받을래요
          </LoadingButton>
        </Stack>
        )}
      </>
      )}
      {step === "filter" && (
      jobPostKeywordSelected ? (
      <>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            아래 키워드가 포함된 공고를 제외하고 원하는 매칭만 제안 받습니다.
          </DialogContentText>
          <Autocomplete
            multiple
            fullWidth
            freeSolo
            clearOnBlur
            disableCloseOnSelect
            handleHomeEndKeys
            value={keywords}
            onChange={handleKeywordChange}
            renderInput={(params) => (
              <TextField 
                {...params}
                fullWidth
                placeholder="제외할 키워드를 입력해주세요."
                helperText={keywords.some(keyword => keyword.keyword.length > 100) ? "키워드는 100글자 미만으로 입력해주세요." : "키워드를 입력 후 Enter를 누르면 추가됩니다."}
                error={keywords.some(keyword => keyword.keyword.length > 100)}
              />
            )}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option.keyword;
            }}
            isOptionEqualToValue={(option, value) => option.keyword === value.keyword}
            renderOption={(props, option) => (
              <MenuItem {...props} key={option.keyword}>
                {option.inputValue ? option.inputValue : option.keyword}
              </MenuItem>
            )}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);

              const matched = filtered.find((option) => option.keyword === params.inputValue);
              if (!matched && params.inputValue !== '') {
                filtered.push({
                  inputValue: `"${params.inputValue}" 키워드 추가하기`,
                  keyword: params.inputValue,
                });
              }
              return filtered;
            }}
            options={systemKeywords?.map((option) => {return {keyword: option.keyword, inputValue: option.inputValue || ''}}) || []}
          />
        </DialogContent>
        {smUp && (
        <DialogActions>
          <Stack spacing={3} direction="row">
            <LoadingButton
              onClick={updateMatchingFilter}
              variant="contained"
              loading={isSubmitting}
              size="large"
            >
              저장하기
            </LoadingButton>
          </Stack>
        </DialogActions>
        )}
        {!smUp && (
        <Stack sx={{ mx: 2, my: 3 }} spacing={2}>
          <LoadingButton
            onClick={updateMatchingFilter}
            variant="contained"
            loading={isSubmitting}
            size="large"
          >
            저장하기
          </LoadingButton>
        </Stack>
        )}
      </>
      ) : (
      <>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            이 공고는 아래 키워드를 바탕으로 추천됐어요.<br />
            앞으로 매칭 받고 싶지 않은 키워드가 있다면, 선택해서 제외해 주세요.
          </DialogContentText>
          <Stack spacing={1}>
          {jobPostKeywords?.map((k, index) => {
            return (
              <Stack
                key={index} direction="row" alignItems="center" spacing={.5}
                sx={{
                  border: 1,
                  borderColor: (theme) => k.selected ? theme.palette.common.black : theme.palette.grey[500_32],
                  backgroundColor: (theme) => k.selected ? theme.palette.grey[200] : 'transparent',
                  borderRadius: 1.5,
                  p: 1,
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'background.neutral',
                    borderColor: 'common.black',
                  },
                }}
                onClick={() => checkJobPostKeyword(k, index)}
              >
                <Checkbox
                  checked={k.selected}
                />
                <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
                  {k.keyword}
                </Typography>
              </Stack>
            )
          })}
          </Stack>
        </DialogContent>
        {smUp && (
        <DialogActions>
          <Stack spacing={3} direction="row">
            <LoadingButton
              onClick={() => setJobPostKeywordSelected(true)}
              loading={isSubmitting}
              size="large"
            >
              건너뛰기
            </LoadingButton>
            <LoadingButton
              onClick={selectJobPostKeywords}
              variant="contained"
              loading={isSubmitting}
              size="large"
              disabled={jobPostKeywords?.filter((k) => k.selected).length === 0}
            >
              {jobPostKeywords?.filter((k) => k.selected).length}개 키워드 제외하기
            </LoadingButton>
          </Stack>
        </DialogActions>
        )}
        {!smUp && (
        <Stack sx={{ mx: 2, my: 3 }} spacing={2}>
          <LoadingButton
            onClick={selectJobPostKeywords}
            variant="contained"
            loading={isSubmitting}
            size="large"
            disabled={jobPostKeywords?.filter((k) => k.selected).length === 0}
          >
            {jobPostKeywords?.filter((k) => k.selected).length}개 키워드 제외하기
          </LoadingButton>
          <LoadingButton
            onClick={() => setJobPostKeywordSelected(true)}
            loading={isSubmitting}
            size="large"
          >
            건너뛰기
          </LoadingButton>
        </Stack>
        )}
      </>
      )
      )}
      </Dialog>
  );
}
